import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Select } from 'antd';

import cn from 'classnames';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import Icon, { CheckedIcon, CloseIcon } from 'components/Atoms/Icon';
import PropTypes from 'prop-types';
import { LoadingOutlined } from '@ant-design/icons';
import { FormattedMessage } from "react-intl";

import './styles.scss';

const MySelect = (props) => {
  const onChange = props.onChange;

  const [iconSuffix, setIconSuffix] = useState(
    <DownOutlined className="icon-caret-custom" />
  );

  const selectRef = useRef(null);

  useEffect(() => {
    if (props.value && props.allowClear) {
      setIconSuffix(
        <Icon
          className="icon-clear-custom"
          component={CloseIcon}
          onClick={onClearValue}
        />
      );
      return;
    }
    setIconSuffix(
      <DownOutlined className="icon-caret-custom" onClick={_focus} />
    );
  }, [props.value]);

  const onClearValue = () => {
    onChange('');
    props.onSelect('');
  };

  const handleChange = (value) => {
    onChange(!props.myLabel ? value : value.value);
    props.onSelect(!props.myLabel ? value : value.value);
  };

  const _focus = () => {
    if (selectRef.current) selectRef.current.focus();
  };

  const _blur = () => {
    if (selectRef.current) selectRef.current.blur();
  };

  const onDropdownVisibleChange = (open) => {
    if (open) {
      setIconSuffix(
        <UpOutlined className="icon-caret-custom" onClick={_blur} />
      );
      return;
    }
    setIconSuffix(
      <DownOutlined className="icon-caret-custom" onClick={_focus} />
    );
  };

  const filterOption = (input, option) =>
    option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;

  const valueLabel = useMemo(() => {
    if (!props.myLabel) return '';
    const el = props.options.find(
      (option, i) => option[props.valueKey] === props.value
    );
    if (el) {
      return el[props.labelKey];
    }
    return '';
  }, [props.value, props.options]);

  return (
    <div id="my-select">
      <Select
        disabled={props.disabled}
        showSearch={props.showSearch}
        onChange={handleChange}
        ref={selectRef}
        id="select-focus"
        size="large"
        placeholder={props.placeholder}
        className={cn('select-custom', props.className)}
        dropdownClassName={props.dropdownClassName}
        labelInValue={props.myLabel}
        value={
          !props.myLabel
            ? props.value || undefined
            : {
                value: props.value || undefined,
                label: (
                  <span>
                    <span className="my-label">{props.myLabel}</span>
                    <span className="my-label-value">{valueLabel}</span>
                  </span>
                ),
              }
        }
        suffixIcon={
          props.disabled ? null : props.loading ? (
            <LoadingOutlined style={{ fontSize: '20px' }} />
          ) : (
            iconSuffix
          )
        }
        onDropdownVisibleChange={onDropdownVisibleChange}
        dropdownMatchSelectWidth={false}
        filterOption={filterOption}
        showAction={['focus', 'click']}
        defaultValue={props.defaultValue}
        dropdownRender={
          props.dropdownRender
            ? (menu) => props.dropdownRender(menu, _blur)
            : undefined
        }
        onSearch={props.onSearch || undefined}
        notFoundContent={props.notFoundContent || undefined}
        onPopupScroll={props.onPopupScroll || undefined}
        maxTagCount={props.maxTagCount}
      >
        {props.options.map((option, idx) => (
          <Select.Option
            key={option[props.valueKey] || idx}
            value={option[props.valueKey]}
            label={option[props.labelKey]}
            className="my-select-option my-select-custom-item"
            disabled={option.isDisable}
          >
            {props.renderItem ? (
              <div className="my-select-custom-item">
                {props.renderItem(option)}
              </div>
            ) : (
              <>
                {option.icon && (
                  <Icon
                    component={option.icon}
                    className="my-select-option-icon"
                  />
                )}

                {option.subText ? (
                  <>
                    <span
                      className="my-select-text"
                      dangerouslySetInnerHTML={{
                        __html: option[props.labelKey],
                      }}
                    ></span>

                    <span style={{ fontSize: 14, marginLeft: 4 }}>
                      (<i>{option.subText}</i>)
                    </span>
                  </>
                ) : (
                  <span
                    className="my-select-text"
                    dangerouslySetInnerHTML={{ __html: option[props.labelKey] }}
                  ></span>
                )}
              </>
            )}
          </Select.Option>
        ))}
      </Select>
      <div id="select-dropdown-container" />
    </div>
  );
};

MySelect.propTypes = {
  size: PropTypes.string,
  labelKey: PropTypes.string,
  valueKey: PropTypes.string,
  options: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  placeholder: PropTypes.string,
  name: PropTypes.string,
  showSearch: PropTypes.bool,
  onSelect: PropTypes.func,
  disabled: PropTypes.bool,
  suffixIcon: PropTypes.any,
  allowClear: PropTypes.bool,
  className: PropTypes.string,
  renderItem: PropTypes.func,
  defaultValue: PropTypes.string,
};

MySelect.defaultProps = {
  placeholder: <FormattedMessage id="common.placeholder_please_select" />,
  labelKey: 'name',
  valueKey: 'id',
  size: 'large',
  options: [
    { name: 'Option 1', id: 1 },
    { name: 'Option 2', id: 2 },
    { name: 'Option 3', id: 3 },
  ],
  name: 'fieldName',
  showSearch: false,
  onSelect: () => null,
  disabled: false,
  allowClear: false,
  defaultValue: null,
};

export default MySelect;
